import { Component, Input } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DataFormModel } from '../../models/data-form-model.model';
import { Platform } from '../../models/platform.model';
import { PlatformService } from '../../services/platform.service';
import { UtilService } from '../../services/util.service';

@Component({
  selector: 'app-platform-modal',
  templateUrl: './platform-modal.component.html',
  styleUrl: './platform-modal.component.scss'
})
export class PlatformModalComponent {

  @Input() platform?: Platform;

  public dataForm: DataFormModel = {
    sku: {
      value: '',
      type: 'text',
      label: 'SKU',
      placeholder: 'CRADLE-RESIDENT-APP',
      requiredMessage: 'Por favor, ingrese el SKU de la plataforma',
      required: true,
      validators: [Validators.required]
    },
    appName: {
      value: '',
      type: 'text',
      label: 'Nombre de la aplicación',
      placeholder: 'CradleResident',
      requiredMessage: 'Por favor, ingrese el nombre de la aplicación',
      required: true,
      validators: [Validators.required]
    },
  };

  public formPlatform!: FormGroup;
  public loading: boolean = false;
  public submitted: boolean = false;

  constructor(
    public activeModal: NgbActiveModal,
    public platformService: PlatformService,
    public utilService: UtilService,
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  get f() {
    return this.formPlatform.controls;
  }

  /*
   ██████╗██████╗ ███████╗ █████╗ ████████╗███████╗
  ██╔════╝██╔══██╗██╔════╝██╔══██╗╚══██╔══╝██╔════╝
  ██║     ██████╔╝█████╗  ███████║   ██║   █████╗
  ██║     ██╔══██╗██╔══╝  ██╔══██║   ██║   ██╔══╝
  ╚██████╗██║  ██║███████╗██║  ██║   ██║   ███████╗
   ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async create() {

    const flag = await this.platformService.create(
      this.formPlatform.value
    );

    if(flag) {

      for(let key of Object.keys(this.dataForm)) {
        this.dataForm[key].value = '';
      }

      this.activeModal.close();
    }

  }

  /*
  ██╗      ██████╗  █████╗ ██████╗ ██████╗  █████╗ ████████╗ █████╗
  ██║     ██╔═══██╗██╔══██╗██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔══██╗
  ██║     ██║   ██║███████║██║  ██║██║  ██║███████║   ██║   ███████║
  ██║     ██║   ██║██╔══██║██║  ██║██║  ██║██╔══██║   ██║   ██╔══██║
  ███████╗╚██████╔╝██║  ██║██████╔╝██████╔╝██║  ██║   ██║   ██║  ██║
  ╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝ ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚═╝  ╚═╝

  */
  async loadData() {
    this.loading = true;

    /* Solo se precarga cuando el modal se abrió SOBRE una plataforma existente.
       En alta el formulario queda vacío: nunca se autorrellena nada que pueda
       sobrescribir un dato ya capturado. */
    if(this.platform) {

      this.dataForm = {
        ...this.dataForm,
      };

      for(let key of Object.keys(this.dataForm)) {

        if(key in this.platform!) {
          const platformKey = key as keyof typeof this.platform;

          this.dataForm[key].value = this.platform[platformKey]!;
        }
      }
    }

    this.formPlatform = this.utilService.buildForm(this.dataForm);

    this.loading = false;
  }

  /*
   ██████╗ ███╗   ██╗███████╗██╗   ██╗██████╗ ███╗   ███╗██╗████████╗
  ██╔═══██╗████╗  ██║██╔════╝██║   ██║██╔══██╗████╗ ████║██║╚══██╔══╝
  ██║   ██║██╔██╗ ██║███████╗██║   ██║██████╔╝██╔████╔██║██║   ██║
  ██║   ██║██║╚██╗██║╚════██║██║   ██║██╔══██╗██║╚██╔╝██║██║   ██║
  ╚██████╔╝██║ ╚████║███████║╚██████╔╝██████╔╝██║ ╚═╝ ██║██║   ██║
   ╚═════╝ ╚═╝  ╚═══╝╚══════╝ ╚═════╝ ╚═════╝ ╚═╝     ╚═╝╚═╝   ╚═╝

  */
  async onSubmit() {

    /* El botón de envío SIEMPRE está habilitado: la validación corre aquí y el
       error se pinta debajo del campo que lo provocó. */
    this.submitted = true;

    if(this.formPlatform.invalid) {
      return;
    }

    if(this.platform) {
      await this.update();
    } else {
      await this.create();
    }

  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async update() {

    const flag = await this.platformService.update({
      platformId: this.platform!.platformId,
      ...this.formPlatform.value
    });

    if(flag) {

      for(let key of Object.keys(this.dataForm)) {
        this.dataForm[key].value = '';
      }

      this.activeModal.close();
    }
  }

}
